import React, { useEffect, useState } from "react"
import { post } from "../../../helpers/api_helper"
import { Smartphone, ShieldCheck, ShieldAlert, AlertTriangle, Lightbulb, Star, CheckCircle2, Layers, Wallet, Users2 } from "lucide-react"
import { ReportCard, ReportSectionHeader, ReportStatTile, ReportEmptyState, ReportSkeleton, ReportBadge } from "../components/ReportUI"
import { REPORT_COLORS } from "../components/reportTheme"
import { buildMetricCards } from "../../../utils/aiReportMetrics"
import { okara_api } from "../../../utils/variables"

const TERMINAL_STATUSES = ["completed", "failed", "error"]

const fmtNum = n => {
  if (n == null) return "—"
  const num = Number(n)
  if (Number.isNaN(num)) return String(n)
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`
  return num.toLocaleString()
}

const fmtCurrency = n => (n == null ? "—" : `$${fmtNum(n)}`)

const fmtPrice = price => {
  if (!price?.currency) return "Free / N/A"
  return price.currency === "USD" ? `$${price.amount}` : `${price.currency} ${price.amount}`
}

// `report.response` is the same structured JSON as the standalone AI Report
// (metrics/key_observations/recommendations/sentiment) — reuse the shared
// metrics builder and key it by label the way this tab's findChange() expects.
const parseMetricChanges = response => {
  const changes = {}
  if (!response || typeof response !== "object") return changes
  for (const card of buildMetricCards(response.metrics)) {
    changes[card.label] = { direction: card.direction === "neutral" ? "flat" : card.direction, delta: card.change }
  }
  return changes
}

const findChange = (changes, ...keywords) => {
  const key = Object.keys(changes).find(k => keywords.some(kw => k.toLowerCase().includes(kw)))
  return key ? changes[key] : null
}

const LOAD_URL = `${okara_api}/api/app/load/`

const pollAppLoad = async body => {
  let res = await post(LOAD_URL, body)
  let attempts = 0
  while (res?.job_status && !TERMINAL_STATUSES.includes(res.job_status) && attempts < 20) {
    await new Promise(r => setTimeout(r, 4000))
    res = await post(LOAD_URL, body)
    attempts++
  }
  return res
}

export default function AICMOSummaryTab({ appId, appName }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)

  useEffect(() => {
    if (!appId) return
    let cancelled = false
    setLoading(true)
    setError(null)
    pollAppLoad({ app_id: appId })
      .then(res => {
        if (cancelled) return
        if (res?.job_status === "completed" || res?.game_info) setData(res)
        else setError("Failed to load AI CMO data")
      })
      .catch(err => !cancelled && setError(err.message || "Failed to load AI CMO data"))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [appId])

  if (!appId) return null
  if (loading) return <ReportSkeleton tiles={4} />
  if (error) return <ReportEmptyState icon={AlertTriangle} title="Failed to Load AI CMO Data" description={error} />
  if (!data) return null

  const info = data.game_info || {}
  const policy = data.policy?.policy_report
  const changes = parseMetricChanges(data.report?.response)
  const competitors = data.similar_apps || []

  const downloadsChange = findChange(changes, "download")
  const revenueChange = findChange(changes, "revenue")
  const kuChange = findChange(changes, "ku")
  const reviewChange = findChange(changes, "review", "urv")

  const complianceStatus = policy?.summary?.compliance_status
  const isCompliant = (complianceStatus || "").toLowerCase() === "compliant"
  const confidence = policy?.summary?.confidence != null ? Math.round(policy.summary.confidence * 100) : null

  const blockingIssues = policy?.blocking_issues || []
  const recommendedImprovements = policy?.recommended_improvements || []
  const storePageGaps = policy?.store_page_gaps || []
  const submissionChecklist = Object.entries(policy?.submission_checklist || {})

  return (
    <div>
      <ReportSectionHeader
        icon={Smartphone}
        eyebrow="AI CMO"
        title={info.name || appName}
        action={
          complianceStatus ? (
            <ReportBadge tone={isCompliant ? "good" : "warning"}>{isCompliant ? "Compliant" : complianceStatus}</ReportBadge>
          ) : null
        }
      />

      <ReportCard style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <img
          src={info.image_url || info.icon || info.portrait || "https://via.placeholder.com/56"}
          alt={info.name}
          style={{ width: 56, height: 56, borderRadius: 14, objectFit: "cover", flexShrink: 0 }}
        />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>{info.name || appName}</div>
          <div style={{ fontSize: 12, color: REPORT_COLORS.textMuted, display: "flex", alignItems: "center", gap: 16, marginTop: 4, flexWrap: "wrap" }}>
            {info.rating?.average != null && (
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Star size={12} color={REPORT_COLORS.warning} /> {Number(info.rating.average).toFixed(2)}
              </span>
            )}
            {(info.category || info.genre?.length > 0) && (
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Layers size={12} /> {info.category || "Games"}{info.genre?.length > 0 && ` · ${info.genre.slice(0, 2).join(", ")}`}
              </span>
            )}
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Wallet size={12} /> {fmtPrice(info.price)}
            </span>
          </div>
        </div>
      </ReportCard>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 16 }}>
        <ReportStatTile
          label="Downloads"
          value={fmtNum(info.analytics?.d)}
          delta={downloadsChange?.direction !== "flat" ? downloadsChange?.delta : null}
          deltaDirection={downloadsChange?.direction}
        />
        <ReportStatTile
          label="Revenue"
          value={fmtCurrency(info.analytics?.revenue)}
          delta={revenueChange?.direction !== "flat" ? revenueChange?.delta : null}
          deltaDirection={revenueChange?.direction}
        />
        <ReportStatTile
          label="Reviews"
          value={fmtNum(info.analytics?.urv)}
          delta={reviewChange?.direction !== "flat" ? reviewChange?.delta : null}
          deltaDirection={reviewChange?.direction}
        />
        <ReportStatTile
          label="KU Score"
          value={info.analytics?.ku != null ? Number(info.analytics.ku).toFixed(2) : "—"}
          delta={kuChange?.direction !== "flat" ? kuChange?.delta : null}
          deltaDirection={kuChange?.direction}
        />
        <ReportStatTile label="Compliance Confidence" value={confidence != null ? `${confidence}%` : "—"} />
      </div>

      {policy && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12, marginBottom: 16 }}>
            <ReportCard>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                {isCompliant ? <ShieldCheck size={14} color={REPORT_COLORS.good} /> : <ShieldAlert size={14} color={REPORT_COLORS.warning} />}
                <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>
                  Blocking Issues {blockingIssues.length ? `(${blockingIssues.length})` : ""}
                </span>
              </div>
              {blockingIssues.length === 0 ? (
                <div style={{ fontSize: 12, color: REPORT_COLORS.textMuted }}>No blocking issues found.</div>
              ) : (
                blockingIssues.map((issue, i) => (
                  <div key={i} style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: 8, paddingBottom: 8, borderBottom: i < blockingIssues.length - 1 ? `1px solid ${REPORT_COLORS.cardBorder}` : "none" }}>
                    <strong style={{ color: REPORT_COLORS.textPrimary }}>{issue.title}</strong>
                    {issue.description && <div style={{ marginTop: 2 }}>{issue.description}</div>}
                  </div>
                ))
              )}
            </ReportCard>

            <ReportCard>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <Lightbulb size={14} color={REPORT_COLORS.warning} />
                <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>
                  Recommended Improvements {recommendedImprovements.length ? `(${recommendedImprovements.length})` : ""}
                </span>
              </div>
              {recommendedImprovements.length === 0 ? (
                <div style={{ fontSize: 12, color: REPORT_COLORS.textMuted }}>No recommendations available.</div>
              ) : (
                recommendedImprovements.map((rec, i) => (
                  <div key={i} style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: 8, paddingBottom: 8, borderBottom: i < recommendedImprovements.length - 1 ? `1px solid ${REPORT_COLORS.cardBorder}` : "none" }}>
                    <strong style={{ color: REPORT_COLORS.textPrimary }}>{rec.title}</strong>
                    {rec.suggested_improvement && <div style={{ marginTop: 2 }}>{rec.suggested_improvement}</div>}
                  </div>
                ))
              )}
            </ReportCard>
          </div>

          {(storePageGaps.length > 0 || submissionChecklist.length > 0) && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12, marginBottom: 16 }}>
              {storePageGaps.length > 0 && (
                <ReportCard>
                  <div style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary, marginBottom: 12 }}>
                    Store Page Gaps ({storePageGaps.length})
                  </div>
                  {storePageGaps.map((gap, i) => (
                    <div key={i} style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: 8, paddingBottom: 8, borderBottom: i < storePageGaps.length - 1 ? `1px solid ${REPORT_COLORS.cardBorder}` : "none" }}>
                      <strong style={{ color: REPORT_COLORS.textPrimary }}>{gap.field}</strong>
                      {gap.issue && <div style={{ marginTop: 2 }}>{gap.issue}</div>}
                      {gap.how_to_fix && <div style={{ marginTop: 2, color: REPORT_COLORS.textMuted, fontStyle: "italic" }}>🔧 {gap.how_to_fix}</div>}
                    </div>
                  ))}
                </ReportCard>
              )}

              {submissionChecklist.length > 0 && (
                <ReportCard>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                    <CheckCircle2 size={14} color={REPORT_COLORS.good} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Submission Checklist</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    {submissionChecklist.map(([key, val]) => {
                      const provided = val === "provided"
                      return (
                        <div key={key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: REPORT_COLORS.textSecondary }}>
                          {provided ? <CheckCircle2 size={12} color={REPORT_COLORS.good} /> : <AlertTriangle size={12} color={REPORT_COLORS.warning} />}
                          {key.replace(/_/g, " ")}
                        </div>
                      )
                    })}
                  </div>
                </ReportCard>
              )}
            </div>
          )}
        </>
      )}

      {competitors.length > 0 && (
        <ReportCard>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <Users2 size={14} color={REPORT_COLORS.textMuted} />
            <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Top Competitors</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 10 }}>
            {competitors.slice(0, 8).map((c, i) => (
              <a
                key={c.item_id || i}
                href={c.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: 8,
                  borderRadius: 10,
                  backgroundColor: REPORT_COLORS.cardBgRaised,
                  textDecoration: "none",
                  minWidth: 0,
                }}
              >
                {c.image_url && <img src={c.image_url} alt={c.name} style={{ width: 24, height: 24, borderRadius: 6, objectFit: "cover", flexShrink: 0 }} />}
                <span style={{ fontSize: 12, color: REPORT_COLORS.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{c.name}</span>
                {c.rating != null && (
                  <span style={{ fontSize: 11, color: REPORT_COLORS.warning, display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
                    <Star size={10} /> {Number(c.rating).toFixed(1)}
                  </span>
                )}
              </a>
            ))}
          </div>
        </ReportCard>
      )}
    </div>
  )
}
